import './SupportMicButton.scss';
import { CSSProperties, useState, useSyncExternalStore } from 'react';
import { MdExpandLess, MdMic, MdMicOff } from 'react-icons/md';
import { REMOTE_CONTROL_EXCLUDE_CLASS, VOICE_DEVICE_TEXT, VOICE_UNAVAILABLE_TEXT } from '@srm/screen-sharing/light';
import SoundSettings, { scaledVoiceLevel } from '@srm/ui/sound-settings';
import Button from '@srm/ui/button';
import Tooltip from '@srm/ui/tooltip';
import AnchoredDialog from '@srm/ui/anchored-dialog';
import type { ISupportSound } from '@providers/ScreenShareProvider/supportSound';
import {
    micFailureHint,
    type ISupportMicLevel,
    type ISupportVoiceState,
    type SupportMicState,
} from '@providers/ScreenShareProvider/supportVoice';
import { SUPPORT_MIC_TOGGLE_ATTRIBUTE } from '@providers/ScreenShareProvider/supportChat';

export interface ISupportMicButtonProps {
    voice: Pick<ISupportVoiceState, 'mic' | 'micError' | 'unavailable'>;
    /** Уровень своего микрофона — индикатор на кнопке, пока он включён */
    level?: ISupportMicLevel;
    onToggle: () => void;
    /** Меню микрофона (IMPLEMENTATION.md §42): устройства, уровень, шумоподавление; без него — только кнопка */
    sound?: ISupportSound;
}

const SETTINGS_LABEL = 'Настройки звука';

/** Индикатор уровня: три столбика, высота — от уровня; перерисовывается сам, без полосы. */
const MicLevelMeter = ({ level }: { level: ISupportMicLevel }) => {
    const value = useSyncExternalStore(level.subscribe, level.get);
    const scaled = scaledVoiceLevel(value);
    return (
        <span
            className="support-bar__mic-level"
            aria-hidden
            data-testid="screen-share-mic-level"
            data-loud={scaled > 0.15 || undefined}
            style={{ '--support-mic-level': scaled.toFixed(2) } as CSSProperties}
        >
            <i />
            <i />
            <i />
        </span>
    );
};

const LABEL: Record<SupportMicState, string> = {
    off: 'Микрофон',
    starting: 'Микрофон',
    on: 'Микрофон',
    denied: 'Нет доступа к микрофону',
    'no-device': 'Микрофон не найден',
    error: 'Микрофон не включился',
};

const HINT: Partial<Record<SupportMicState, string>> = {
    off: 'Включить микрофон — поддержка вас услышит',
    starting: 'Браузер спрашивает разрешение на микрофон',
    on: 'Выключить микрофон',
};

/**
 * Микрофон пользователя в полосе показа (IMPLEMENTATION.md §31): выключен по умолчанию; включён — зелёный; нет доступа —
 * подсказка, как разрешить; в режиме снимков — недоступен.
 */
const SupportMicButton = ({ voice, level, onToggle, sound }: ISupportMicButtonProps) => {
    const [opened, setOpened] = useState(false);
    const snapshots = voice.unavailable === 'snapshots';
    const state = snapshots ? 'off' : voice.mic;
    const hint = snapshots ? VOICE_UNAVAILABLE_TEXT.snapshots : (micFailureHint(voice) ?? HINT[state] ?? '');
    const button = (
        <Tooltip maxWidth={320} label={hint}>
            {/* В режиме снимков кнопка остаётся в порядке Tab (`aria-disabled`): причина — в подсказке по фокусу. */}
            <Button
                size="sm"
                variant="outline-neutral"
                className="support-bar__mic"
                data-mic={snapshots ? 'unavailable' : state}
                data-testid="screen-share-mic"
                {...{ [SUPPORT_MIC_TOGGLE_ATTRIBUTE]: '' }}
                aria-disabled={snapshots || undefined}
                aria-pressed={state === 'on'}
                aria-label={LABEL[state]}
                loading={state === 'starting'}
                startAdornment={state === 'on' ? <MdMic size={18} /> : <MdMicOff size={18} />}
                endAdornment={state === 'on' && level ? <MicLevelMeter level={level} /> : undefined}
                onClick={snapshots || state === 'starting' ? undefined : onToggle}
            >
                <span className="support-bar__mic-label">{LABEL[state]}</span>
            </Button>
        </Tooltip>
    );
    // В режиме снимков голоса нет — и выбирать нечего.
    if (!sound || snapshots) return button;
    return (
        <span className="support-bar__mic-group">
            {button}
            {/* Меню — в слое оверлеев (хост порталов полосы): вне захвата и вне глушения ввода; управление его не нажмёт. */}
            <AnchoredDialog
                opened={opened}
                onClose={() => setOpened(false)}
                placement="bottom"
                align="right"
                width={320}
                className={REMOTE_CONTROL_EXCLUDE_CLASS}
                dropdownAria={{ 'aria-label': SETTINGS_LABEL }}
                data-testid="screen-share-sound-settings"
                target={
                    <span className="support-bar__mic-more-wrap">
                        <Tooltip wrapTarget={false} disabled={opened} label="Микрофон, динамики, шумоподавление">
                            <Button
                                size="sm"
                                variant="outline-neutral"
                                className="support-bar__mic-more"
                                aria-label={SETTINGS_LABEL}
                                aria-haspopup="dialog"
                                aria-expanded={opened}
                                data-testid="screen-share-mic-settings"
                                startAdornment={<MdExpandLess size={18} aria-hidden />}
                                onClick={() => setOpened((current) => !current)}
                            />
                        </Tooltip>
                    </span>
                }
            >
                <SoundSettings
                    devices={sound.devices}
                    input={sound.prefs.input}
                    output={sound.prefs.output}
                    noiseSuppression={sound.prefs.noiseSuppression}
                    outputSelectable={sound.outputSelectable}
                    onInputChange={sound.chooseInput}
                    onOutputChange={sound.chooseOutput}
                    onNoiseSuppressionChange={sound.setNoiseSuppression}
                    micOn={voice.mic === 'on'}
                    level={level}
                    texts={VOICE_DEVICE_TEXT}
                    data-testid="support-sound"
                />
            </AnchoredDialog>
        </span>
    );
};

export default SupportMicButton;
